import React, { useEffect } from 'react';
import 'react-circular-progressbar/dist/styles.css';
import { useDispatch } from 'react-redux';
import Spinner from '../../components/common/Spinner';
import { useAppSelector } from '../../app/hooks';
import { getEventCruisesPromo } from '../../slices/event-slice';
import { useTranslation } from 'react-i18next';

import { Desktop, Mobile } from '../../App';

import { formatVP } from '../../helpers/helperFunctions';
import {
  CaptionText,
  FooterText,
  H2,
  H3,
  H4,
  P2,
  P3
} from '../../styles/FontStyles';
import {
  Button,
  Flexed,
  ProgressBarWithPercentage,
  SectionSeparator
} from '../../styles/GlobalComponents';
import { color } from '../../styles/GlobalStyles';
import {
  Icon,
  IconArrowPolygon,
  IconCheckRanking,
  IconTier5,
  IconTier6,
  IconTier7,
  IconTier8
} from '../../styles/Icons';
import { Flex, ViewBackOffice } from '../../styles/stylesStyled';
import {
  ArrowTier,
  CheckboxRank,
  CountdownNumbers,
  MavieRank,
  PromotionStartEnd,
  TierBars,
  TierCircle,
  TierReward
} from './EventPromoStyled';

import cruisesPromo from '../../assets/images/backoffice/cruises/extendedCruises.png';
import cruisesPromoMobile from '../../assets/images/backoffice/cruises/extendedCruisesMobile.png';

import Countdown from 'react-countdown';
import { CountdownCircleTimer } from 'react-countdown-circle-timer';

export const EventPromoCruises = () => {
  const { t } = useTranslation('packages');
  const dispatch = useDispatch();

  const { loading, events } = useAppSelector((state) => state.events);

  useEffect(() => {
    dispatch(getEventCruisesPromo());
  }, [dispatch]);

  if (loading) {
    return <Spinner fullScreen />;
  }

  const IconTiers = (id: number) => {
    if (id === 1) {
      return <IconTier5 />;
    } else if (id === 2) {
      return <IconTier6 />;
    } else if (id === 3) {
      return <IconTier7 />;
    } else if (id === 4) {
      return <IconTier8 />;
    }
  };

  const getProgressLabelPosition = (id: number) => {
    if (id === 1) {
      return { position: 'absolute', left: 0, top: -22 };
    } else if (id === 2) {
      return { position: 'absolute', right: '75%', top: -22 };
    } else if (id === 3) {
      return { position: 'absolute', right: '50%', top: -22 };
    } else if (id === 4) {
      return { position: 'absolute', right: '0', top: -22 };
    }
  };

  const getProgressWidth = (id: number) => {
    if (id === 1) {
      return '5%';
    } else if (id === 2) {
      return '20%';
    } else if (id === 3) {
      return '25%';
    } else if (id === 4) {
      return '50%';
    }
  };

  interface HasConditionProps {
    hasCondition: boolean;
  }

  const IconArrow: React.FC<HasConditionProps> = ({ hasCondition }) => {
    return (
      <Icon
        style={{ transform: 'rotate(270deg)' }}
        color={
          hasCondition ? color.colors.primary900 : color.colors.neutral400
        }>
        <IconArrowPolygon />
      </Icon>
    );
  };

  const date = new Date(events.endDate);
  const timestamp = date.getTime();

  interface RendererProps {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    completed: boolean;
  }

  const renderer: React.FC<RendererProps> = ({
    days,
    hours,
    minutes,
    seconds,
    completed
  }: any) => {
    return (
      <div
        style={{
          width: 212,
          height: 212,
          position: 'relative',
          border: '3px solid #DBCEA2',
          borderRadius: '50%',
          padding: 3,
          background:
            'linear-gradient(316.54deg, #FADF7F -251.77%, #ECDEAF -92.56%, #000000 84.14%)'
        }}>
        <CountdownCircleTimer
          isPlaying={!completed}
          duration={60}
          colors={['#ffffff', '#ffffff']}
          trailColor={'#DBCEA2'}
          colorsTime={[60, 60]}
          onComplete={() => {
            // do your stuff here
            return { shouldRepeat: true, delay: 0 }; // repeat animation in 1.5 seconds
          }}
          initialRemainingTime={seconds}
          size={200}
          strokeWidth={15}
          strokeLinecap={'square'}></CountdownCircleTimer>
        <div
          style={{
            width: 200,
            height: 200,
            position: 'absolute',
            top: 0,
            left: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
          <CountdownNumbers ticketPromotion={false}>
            <div>
              <H3>{days}</H3>
              <CaptionText caps> {t('days')}</CaptionText>
            </div>
            <div>
              <H3>{hours}</H3>
              <CaptionText caps> {t('hours')}</CaptionText>
            </div>
            <div>
              <H3>{minutes}</H3>
              <CaptionText caps> {t('minutes')}</CaptionText>
            </div>
          </CountdownNumbers>
        </div>
      </div>
    );
  };

  const showPersonalSalesTierAmount = (i: number, item: any) => {
    if (i === 0 && events?.personalSalesAmount <= 7000) {
      return <div key={i}>{formatVP(events?.personalSalesAmount)}</div>;
    } else if (
      i === 1 &&
      events?.personalSalesAmount > 7000 &&
      events?.personalSalesAmount <= 10000
    ) {
      return <div key={i}>{formatVP(events?.personalSalesAmount)}</div>;
    } else if (
      i === 2 &&
      events?.personalSalesAmount > 10000 &&
      events?.personalSalesAmount <= 20000
    ) {
      return <div key={i}>{formatVP(events?.personalSalesAmount)}</div>;
    } else if (
      i === 3 &&
      events?.personalSalesAmount > 20000 &&
      events?.personalSalesAmount <= 25000
    ) {
      return <div key={i}>{formatVP(events?.personalSalesAmount)}</div>;
    } else if (
      i === 3 &&
      events?.personalSalesAmount > item.personalSalesProgress.required
    ) {
      return <div key={i}>{formatVP(item.personalSalesProgress.required)}</div>;
    }
  };

  const showDirectVolumeTierAmount = (i: number, item: any) => {
    if (events?.directVolumeAmount <= 28000 && i === 0) {
      return <div key={i}>{formatVP(events?.directVolumeAmount)}</div>;
    } else if (
      i === 1 &&
      events?.directVolumeAmount > 28000 &&
      events?.directVolumeAmount <= 40000
    ) {
      return <div key={i}>{formatVP(events?.directVolumeAmount)}</div>;
    } else if (
      i === 2 &&
      events?.directVolumeAmount > 40000 &&
      events?.directVolumeAmount <= 80000
    ) {
      return <div key={i}>{formatVP(events?.directVolumeAmount)}</div>;
    } else if (
      i === 3 &&
      events?.directVolumeAmount > 80000 &&
      events?.directVolumeAmount <= 100000
    ) {
      return <div key={i}>{formatVP(events?.directVolumeAmount)}</div>;
    } else if (
      i === 3 &&
      events?.directVolumeAmount > item.directVolumeProgres.required
    ) {
      return <div key={i}>{formatVP(item.directVolumeProgres.required)}</div>;
    }
  };

  const showPaidTeamTierAmount = (i: number, item: any) => {
    if (events?.paidTeamAmount <= 28000 && i === 0) {
      return <div key={i}>{formatVP(events?.paidTeamAmount)}</div>;
    } else if (
      i === 1 &&
      events?.paidTeamAmount > 28000 &&
      events?.paidTeamAmount <= 40000
    ) {
      return <div key={i}>{formatVP(events?.paidTeamAmount)}</div>;
    } else if (
      i === 2 &&
      events?.paidTeamAmount > 40000 &&
      events?.paidTeamAmount <= 80000
    ) {
      return <div key={i}>{formatVP(events?.paidTeamAmount)}</div>;
    } else if (
      i === 3 &&
      events?.paidTeamAmount > 80000 &&
      events?.paidTeamAmount <= 100000
    ) {
      return <div key={i}>{formatVP(events?.paidTeamAmount)}</div>;
    } else if (
      i === 3 &&
      events?.paidTeamAmount > item.paidTeamProgres.required
    ) {
      return <div key={i}>{formatVP(item.paidTeamProgres.required)}</div>;
    }
  };

  return (
    <ViewBackOffice>
      <Desktop>
        <MavieRank>
          <section style={{ position: 'relative' }}>
            <img
              src={cruisesPromo}
              alt='dubaiPromo'
              style={{ width: '100%' }}
            />
            <div style={{ position: 'absolute', right: 50, top: 20 }}>
              <Countdown date={timestamp} renderer={renderer} />
            </div>
          </section>

          <div>
            <Flexed center between>
              <P2 color={color.colors.neutral500} maxWidth='70%'>
                {`Get ready for an extraordinary Mediterranean adventure with MaVie! Dive into a world of luxury from May 13-16, 2024, and reward yourself for your exceptional work. Enjoy in the stunning views of Genoa, Civitavecchia, Cannes, and Barcelona. Secure your spot with our 60-day promotion kicking off on February 5th. Don't miss out on your chance to party, connect, and relax, all wrapped up in a voyage of a lifetime.`}
              </P2>
              <Button
                w='200px'
                disabled={true}
                // onClick={() => dispatch(claimDubaiPromo())}>
              >
                Claim reward
              </Button>
            </Flexed>
            <SectionSeparator margin='40px 0' />
            <PromotionStartEnd>
              <div>
                <CaptionText caps mb='8' color={color.colors.neutral500}>
                  promotion starts
                </CaptionText>
                <H3>February 5, 2024, 00:00 UTC</H3>
              </div>

              <div>
                <CaptionText caps mb='8' color={color.colors.neutral500}>
                  promotion ends
                </CaptionText>
                <H3>April 5, 2024, 23:59 UTC</H3>
              </div>

              <div>
                <CaptionText caps mb='8' color={color.colors.neutral500}>
                  valid
                </CaptionText>
                <H2>60 days</H2>
              </div>
            </PromotionStartEnd>
            <CaptionText caps color={color.colors.neutral500} mb='12' mt='50'>
              rewards
            </CaptionText>
            {events?.tiers?.map((item, i) => {
              return (
                <TierReward
                  color={
                    item.hasConditionForTier
                      ? color.colors.primary900
                      : color.colors.neutral400
                  }
                  key={i}>
                  <span>
                    <Icon>{IconTiers(i + 1)}</Icon>
                  </span>
                  <P3
                    color={
                      item.hasConditionForTier
                        ? color.colors.primary900
                        : color.colors.neutral400
                    }
                    bold={item.hasConditionForTier}>
                    {item.name} - {item.reward}
                  </P3>
                  {item.hasConditionForTier && (
                    <CheckboxRank color={color.colors.primary900}>
                      <Icon color={color.colors.neutral50}>
                        <IconCheckRanking />
                      </Icon>
                    </CheckboxRank>
                  )}
                </TierReward>
              );
            })}
            <div
              style={{
                display: 'flex',
                marginTop: '50px',
                alignItems: 'center',
                position: 'relative',
                height: 100
              }}>
              <div
                style={{
                  position: 'absolute',
                  left: 'calc(5% - 22px)',
                  alignItems: 'center',
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                <TierCircle
                  color={
                    events?.tiers && events?.tiers[0]?.hasConditionForTier
                      ? color.colors.primary900
                      : color.colors.neutral400
                  }>
                  <Icon>{IconTiers(1)}</Icon>
                </TierCircle>
                <P2 bold>Tier 1</P2>
              </div>
              <div
                style={{
                  position: 'absolute',
                  left: 'calc(25% - 22px)',
                  alignItems: 'center',
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                <TierCircle
                  color={
                    events?.tiers && events?.tiers[1]?.hasConditionForTier
                      ? color.colors.primary900
                      : color.colors.neutral400
                  }>
                  <Icon>{IconTiers(2)}</Icon>
                </TierCircle>
                <P2 bold>Tier 2</P2>
              </div>
              <div
                style={{
                  position: 'absolute',
                  left: 'calc(50% - 22px)',
                  alignItems: 'center',
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                <TierCircle
                  color={
                    events?.tiers && events?.tiers[2]?.hasConditionForTier
                      ? color.colors.primary900
                      : color.colors.neutral400
                  }>
                  <Icon>{IconTiers(3)}</Icon>
                </TierCircle>
                <P2 bold>Tier 3</P2>
              </div>
              <div
                style={{
                  position: 'absolute',
                  left: 'calc(95% - 22px)',
                  alignItems: 'center',
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                <TierCircle
                  color={
                    events?.tiers && events?.tiers[3]?.hasConditionForTier
                      ? color.colors.primary900
                      : color.colors.neutral400
                  }>
                  <Icon>{IconTiers(4)}</Icon>
                </TierCircle>
                <P2 bold>Tier 4</P2>
              </div>
              <ArrowTier
                style={{
                  position: 'absolute',
                  left: 'calc(5% + 10px)',
                  right: 'calc(75% + 10px)'
                }}
                hasCondition={
                  events?.tiers && events?.tiers[0]?.hasConditionForTier
                }></ArrowTier>
              <ArrowTier
                style={{
                  position: 'absolute',
                  left: 'calc(25% + 10px)',
                  right: 'calc(50% + 10px)'
                }}
                hasCondition={
                  events?.tiers && events?.tiers[1]?.hasConditionForTier
                }
              />
              <ArrowTier
                style={{
                  position: 'absolute',
                  left: 'calc(50% + 10px)',
                  right: 'calc(95% + 10px)',
                  width: 'calc(45% - 60px)'
                }}
                hasCondition={
                  events?.tiers && events?.tiers[2].hasConditionForTier
                }
              />
              <div
                style={{
                  position: 'absolute',
                  right: 'calc(75%  + 20px)'
                }}>
                <IconArrow
                  hasCondition={
                    events?.tiers && events?.tiers[0]?.hasConditionForTier
                  }
                />
              </div>
              <div
                style={{
                  position: 'absolute',
                  right: 'calc(50%  + 20px)'
                }}>
                <IconArrow
                  hasCondition={
                    events?.tiers && events?.tiers[1]?.hasConditionForTier
                  }
                />
              </div>
              <div
                style={{
                  position: 'absolute',
                  right: 'calc(5%  + 20px)'
                }}>
                <IconArrow
                  hasCondition={
                    events?.tiers && events?.tiers[2]?.hasConditionForTier
                  }
                />
              </div>
            </div>
            <CaptionText caps color={color.colors.neutral500} mb='12' mt='50'>
              Track your progress
            </CaptionText>
            <P3 semiBold color={color.colors.neutral900} mb='20'>
              Personal Sales
            </P3>
            <CaptionText color={color.colors.neutral900} mb='32'>
              Current: {formatVP(events.personalSalesAmount)} USDT
            </CaptionText>
            <div style={{ position: 'relative' }}>
              {events?.tiers?.map((item, i) => {
                return (
                  <FooterText
                    key={i}
                    style={getProgressLabelPosition(i + 1)}
                    color={color.colors.neutral700}>
                    Required: {formatVP(item.personalSalesProgress.required)}{' '}
                    USDT
                  </FooterText>
                );
              })}
            </div>

            <TierBars>
              {events?.tiers?.map((item, i) => {
                return (
                  <>
                    <div style={{ width: getProgressWidth(i + 1) }}>
                      <ProgressBarWithPercentage
                        percentage={item.personalSalesProgress.percentage}
                        color={color.colors.success500}>
                        <div>{showPersonalSalesTierAmount(i, item)}</div>
                      </ProgressBarWithPercentage>
                    </div>
                  </>
                );
              })}
            </TierBars>
            <P3 semiBold color={color.colors.neutral900} mb='12' mt='20'>
              Unilevel Volume
            </P3>
            <CaptionText color={color.colors.neutral900} mb='32'>
              Current: {formatVP(events.directVolumeAmount)} USDT
            </CaptionText>
            <div style={{ position: 'relative' }}>
              {events?.tiers?.map((item, i) => {
                return (
                  <FooterText
                    key={i}
                    style={getProgressLabelPosition(i + 1)}
                    color={color.colors.neutral700}>
                    Required: {formatVP(item.directVolumeProgress.required)}{' '}
                    USDT
                  </FooterText>
                );
              })}
            </div>
            <TierBars>
              {events?.tiers?.map((item, i) => {
                return (
                  <>
                    <div style={{ width: getProgressWidth(i + 1) }}>
                      <ProgressBarWithPercentage
                        percentage={item.directVolumeProgress.percentage}
                        color={color.colors.success500}>
                        <div>{showDirectVolumeTierAmount(i, item)}</div>
                      </ProgressBarWithPercentage>
                    </div>
                  </>
                );
              })}
            </TierBars>
            <P3 semiBold color={color.colors.neutral900} mb='12' mt='20'>
              Paid Team
            </P3>
            <CaptionText color={color.colors.neutral900} mb='32'>
              Current: {formatVP(events.paidTeamAmount)} USDT
            </CaptionText>
            <div style={{ position: 'relative' }}>
              {events?.tiers?.map((item, i) => {
                return (
                  <FooterText
                    key={i}
                    style={getProgressLabelPosition(i + 1)}
                    color={color.colors.neutral700}>
                    Required: {formatVP(item.paidTeamProgress.required)} USDT
                  </FooterText>
                );
              })}
            </div>
            <TierBars>
              {events?.tiers?.map((item, i) => {
                return (
                  <>
                    <div style={{ width: getProgressWidth(i + 1) }}>
                      <ProgressBarWithPercentage
                        percentage={item.paidTeamProgress.percentage}
                        color={color.colors.success500}>
                        <div>{showPaidTeamTierAmount(i, item)}</div>
                      </ProgressBarWithPercentage>
                    </div>
                  </>
                );
              })}
            </TierBars>
          </div>
        </MavieRank>
      </Desktop>

      <Mobile>
        <MavieRank>
          <section style={{ position: 'relative' }}>
            <img
              src={cruisesPromoMobile}
              alt='cruisesPromo'
              style={{ width: '100%' }}
            />
          </section>

          <div>
            <P2 color={color.colors.neutral500} mb='32'>
              {`Get ready for an extraordinary Mediterranean adventure with MaVie! Dive into a world of luxury from May 13-16, 2024, and reward yourself for your exceptional work. Enjoy in the stunning views of Genoa, Civitavecchia, Cannes, and Barcelona. Secure your spot with our 60-day promotion kicking off on February 5th. Don't miss out on your chance to party, connect, and relax, all wrapped up in a voyage of a lifetime.`}
            </P2>
            <Button disabled={true} onClick={() => {}}>
              Claim reward
            </Button>
            <CaptionText caps mb='4' mt='32' color={color.colors.neutral500}>
              promotion starts
            </CaptionText>
            <H3 mb='16'>February 5, 2024, 00:00 UTC</H3>

            <CaptionText caps mb='4' color={color.colors.neutral500}>
              promotion ends
            </CaptionText>
            <H3 mb='16'>April 5, 2024, 23:59 UTC</H3>

            <CaptionText caps mb='4' color={color.colors.neutral500}>
              valid
            </CaptionText>
            <H3 mb='16'>60 days</H3>
            <CaptionText caps color={color.colors.neutral500} mb='12' mt='50'>
              rewards
            </CaptionText>
            {events?.tiers?.map((item, i) => {
              return (
                <TierReward
                  color={
                    item.hasConditionForTier
                      ? color.colors.primary900
                      : color.colors.neutral400
                  }
                  key={i}>
                  <span>
                    <Icon>{IconTiers(i + 1)}</Icon>
                  </span>
                  <P3
                    color={
                      item.hasConditionForTier
                        ? color.colors.primary900
                        : color.colors.neutral400
                    }
                    bold={item.hasConditionForTier}>
                    {item.name} - {item.reward}
                  </P3>
                  {item.hasConditionForTier && (
                    <CheckboxRank color={color.colors.primary900}>
                      <Icon color={color.colors.neutral50}>
                        <IconCheckRanking />
                      </Icon>
                    </CheckboxRank>
                  )}
                </TierReward>
              );
            })}
            {events?.tiers?.map((item, i) => {
              return (
                <div key={i}>
                  <Flexed center m='40px 0 20px 0'>
                    <TierCircle
                      color={
                        item.hasConditionForTier
                          ? color.colors.primary900
                          : color.colors.neutral400
                      }>
                      <Icon>{IconTiers(i + 1)}</Icon>
                    </TierCircle>
                    <P2
                      bold
                      ml='16'
                      mr='16'
                      color={
                        item.hasConditionForTier
                          ? color.colors.primary900
                          : color.colors.neutral400
                      }>
                      {item.name}
                    </P2>
                    {item.hasConditionForTier && (
                      <CheckboxRank color={color.colors.primary900}>
                        <Icon color={color.colors.neutral50}>
                          <IconCheckRanking />
                        </Icon>
                      </CheckboxRank>
                    )}
                  </Flexed>
                  <CaptionText caps color={color.colors.neutral500} mb='4'>
                    Reward
                  </CaptionText>
                  <P2 mb='16' bold>
                    {item.reward}
                  </P2>
                  <CaptionText caps color={color.colors.neutral500} mb='4'>
                    Track your progress
                  </CaptionText>
                  <P3 mb='12'>Personal Sales</P3>
                  <Flexed between>
                    <CaptionText mb='10' color={color.colors.neutral700}>
                      Current: {formatVP(events.personalSalesAmount)} USDT
                    </CaptionText>
                    <CaptionText mb='10' color={color.colors.neutral700}>
                      Required: {formatVP(item.personalSalesProgress.required)}{' '}
                      USDT
                    </CaptionText>
                  </Flexed>
                  <ProgressBarWithPercentage
                    percentage={item.personalSalesProgress.percentage}
                    color={color.colors.success500}>
                    <div>
                      <div>{formatVP(events.personalSalesAmount)}</div>
                    </div>
                  </ProgressBarWithPercentage>
                  <P3 mb='12'>Unilevel Volume</P3>
                  <Flexed between>
                    <CaptionText mb='10' color={color.colors.neutral700}>
                      Current: {formatVP(events.directVolumeAmount)} USDT
                    </CaptionText>
                    <CaptionText mb='10' color={color.colors.neutral700}>
                      Required: {formatVP(item.directVolumeProgress.required)}{' '}
                      USDT
                    </CaptionText>
                  </Flexed>
                  <ProgressBarWithPercentage
                    percentage={item.directVolumeProgress.percentage}
                    color={color.colors.success500}>
                    <div>
                      <div>{formatVP(events.directVolumeAmount)}</div>
                    </div>
                  </ProgressBarWithPercentage>
                  <P3 mb='12'>Paid Team</P3>
                  <Flexed between>
                    <CaptionText mb='10' color={color.colors.neutral700}>
                      Current: {formatVP(events.paidTeamAmount)} USDT
                    </CaptionText>
                    <CaptionText mb='10' color={color.colors.neutral700}>
                      Required: {formatVP(item.paidTeamProgress.required)} USDT
                    </CaptionText>
                  </Flexed>
                  <ProgressBarWithPercentage
                    percentage={item.paidTeamProgress.percentage}
                    color={color.colors.success500}>
                    <div>
                      <div>{formatVP(events.paidTeamAmount)}</div>
                    </div>
                  </ProgressBarWithPercentage>
                </div>
              );
            })}
          </div>
        </MavieRank>
      </Mobile>
    </ViewBackOffice>
  );
};
